import React, { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import styles from "./BillingHistory.module.scss";
import { getBillingHistory } from "../services/api";

type InvoiceStatus = "paid" | "pending" | "failed";

interface Invoice {
  invoiceId: string;
  date: string; // ISO string
  plan: string;
  period?: string;
  amount: number; // major units (EUR)
  status: InvoiceStatus;
  invoiceUrl?: string;
  stripeInvoiceId?: string;
}

const EUR = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const DATE = (iso: string) =>
  new Date(iso).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "2-digit" });

/** Optional: expose a Stripe billing portal URL in your env */
const BILLING_PORTAL_URL =
  (import.meta as any)?.env?.VITE_BILLING_PORTAL_URL ||
  (process.env as any)?.REACT_APP_BILLING_PORTAL_URL ||
  "";

function is404(err: any) {
  return (
    err?.response?.status === 404 ||
    err?.status === 404 ||
    (typeof err?.message === "string" && err.message.includes("404"))
  );
}

const BillingHistory: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | InvoiceStatus>("all");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getBillingHistory(); // expected: { invoices: [...] }
        const normalized: Invoice[] = (data?.invoices || []).map((r: any) => ({
          invoiceId: r.invoiceId || r.id || "",
          date:
            typeof r.date === "string"
              ? r.date
              : r.date
              ? new Date(r.date).toISOString()
              : new Date().toISOString(),
          plan: r.plan || "—",
          period: r.period || "",
          amount: Number(r.amount ?? 0),
          status: (r.status as InvoiceStatus) || "paid",
          invoiceUrl: r.invoiceUrl || r.hosted_invoice_url || undefined,
          stripeInvoiceId: r.stripeInvoiceId || r.invoiceId || r.id,
        }));
        if (!alive) return;
        setInvoices(normalized);
      } catch (err: any) {
        if (!alive) return;
        // In beta, treat 404 as "no invoices yet" (no error banner)
        if (is404(err)) {
          setInvoices([]);
          setError("");
        } else {
          setError(err?.message || "Impossible de charger la facturation.");
          setInvoices([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const downloadInvoice = (inv: Invoice) => {
    if (inv.invoiceUrl) {
      window.open(inv.invoiceUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // Fallback PDF
    const doc = new jsPDF();

    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, 210, 40, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("ReviewBoost AI", 20, 25);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Gestion intelligente des avis clients", 20, 35);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("FACTURE", 20, 70);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Facture #:", 20, 90);
    doc.setFont("helvetica", "normal");
    doc.text(inv.invoiceId.slice(-12), 50, 90);

    doc.setFont("helvetica", "bold");
    doc.text("Date:", 20, 100);
    doc.setFont("helvetica", "normal");
    doc.text(DATE(inv.date), 50, 100);

    doc.setFont("helvetica", "bold");
    doc.text("Statut:", 20, 110);
    doc.setFont("helvetica", "normal");
    doc.text(inv.status === "paid" ? "Payé" : inv.status === "pending" ? "En attente" : "Échoué", 50, 110);

    doc.setDrawColor(200, 200, 200);
    doc.line(20, 140, 190, 140);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("DÉTAILS DE LA FACTURE", 20, 160);

    doc.setFillColor(245, 245, 245);
    doc.rect(20, 170, 170, 10, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Description", 25, 178);
    doc.text("Période", 100, 178);
    doc.text("Montant", 150, 178);

    doc.setDrawColor(200, 200, 200);
    doc.rect(20, 180, 170, 15, "S");

    const planDescription = inv.plan || "Abonnement";
    doc.setFont("helvetica", "normal");
    doc.text(planDescription, 25, 190);
    doc.text(inv.period || "—", 100, 190);
    doc.text(EUR.format(inv.amount), 150, 190);

    doc.setFont("helvetica", "bold");
    doc.text("TOTAL:", 120, 240);
    doc.setFont("helvetica", "normal");
    doc.text(EUR.format(inv.amount), 170, 240);

    const dateStr = new Date(inv.date).toISOString().split("T")[0];
    doc.save(`facture-${inv.invoiceId}-${dateStr}.pdf`);
  };

  const getStatusClass = (s: InvoiceStatus) =>
    s === "paid" ? styles.statusPaid : s === "pending" ? styles.statusPending : styles.statusFailed;

  const getStatusText = (s: InvoiceStatus) => (s === "paid" ? "Payé" : s === "pending" ? "En attente" : "Échoué");

  const filtered = useMemo(
    () => invoices.filter((i) => filter === "all" || i.status === filter),
    [invoices, filter]
  );

  const totals = useMemo(
    () => ({
      count: invoices.length,
      paid: invoices.filter((i) => i.status === "paid").length,
      sum: invoices.reduce((sum, i) => sum + i.amount, 0),
    }),
    [invoices]
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Chargement de l&apos;historique de facturation…</p>
        </div>
      </div>
    );
  }

  const showBetaHint = !error && invoices.length === 0;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Historique de Facturation</h1>
          <p>Consultez et téléchargez vos factures</p>
        </div>

        {BILLING_PORTAL_URL && (
          <a
            href={BILLING_PORTAL_URL}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.portalBtn}
          >
            Gérer la facturation
          </a>
        )}
      </header>

      {error && (
        <div className={styles.error} role="alert">
          {error}{" "}
          <button type="button" className={styles.retryBtn} onClick={() => window.location.reload()}>
            Réessayer
          </button>
        </div>
      )}

      {showBetaHint && (
         
        <div
          style={{
            background: "#eff6ff",
            color: "#1e40af",
            border: "1px solid #bfdbfe",
            padding: "10px 14px",
            borderRadius: 8,
            marginBottom: 16,
          }}
          role="note"
        >
          <strong>Mode bêta :</strong> l’historique des factures n’est pas encore disponible pour votre compte.
          Vous verrez vos factures ici dès la première facturation.
        </div>
      )}

      <div className={styles.filters} role="tablist" aria-label="Filtrer par statut">
        {(["all", "paid", "pending", "failed"] as const).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={filter === k}
            className={`${styles.filterBtn} ${filter === k ? styles.active : ""}`}
            onClick={() => setFilter(k)}
          >
            {k === "all" ? "Toutes" : k === "paid" ? "Payées" : k === "pending" ? "En attente" : "Échouées"} (
            {k === "all" ? invoices.length : invoices.filter((i) => i.status === k).length})
          </button>
        ))}
      </div>

      <section className={styles.invoiceList} aria-live="polite">
        {filtered.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📄</div>
            <h3>Aucune facture trouvée</h3>
            <p>Vous n’avez pas encore de factures.</p>
          </div>
        ) : (
          filtered.map((inv) => (
            <article key={inv.invoiceId} className={styles.invoiceCard}>
              <div className={styles.invoiceInfo}>
                <div className={styles.invoiceHeader}>
                  <h3>Facture #{inv.invoiceId.slice(-8)}</h3>
                  <span className={`${styles.status} ${getStatusClass(inv.status)}`}>
                    {getStatusText(inv.status)}
                  </span>
                </div>

                <div className={styles.invoiceDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Date:</span>
                    <span className={styles.value}>{DATE(inv.date)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Plan:</span>
                    <span className={styles.value}>{inv.plan}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Période:</span>
                    <span className={styles.value}>{inv.period || "—"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Montant:</span>
                    <span className={styles.amount}>{EUR.format(inv.amount)}</span>
                  </div>
                </div>
              </div>

              <div className={styles.invoiceActions}>
                {inv.invoiceUrl ? (
                  <a
                    href={inv.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.downloadBtn}
                  >
                    Ouvrir la facture
                  </a>
                ) : (
                  <button type="button" onClick={() => downloadInvoice(inv)} className={styles.downloadBtn}>
                    📄 Télécharger (PDF)
                  </button>
                )}
              </div>
            </article>
          ))
        )}
      </section>

      <aside className={styles.summary}>
        <div className={styles.summaryCard}>
          <h3>Résumé</h3>
          <div className={styles.summaryRow}>
            <span>Total des factures:</span>
            <span>{totals.count}</span>
          </div>
          <div className={styles.summaryRow}>
            <span>Montant total:</span>
            <span>{EUR.format(totals.sum)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span>Factures payées:</span>
            <span>{totals.paid}</span>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default BillingHistory;
